import serialize from 'form-serialize';
import addToCartInSequence from '../../utils/addToCartInSequence';

export interface AjaxAddElement extends HTMLElement {
  ajaxadd?: AjaxAdd;
}

export interface AjaxAddOptions {
  buttonEl: string;
  buttonTextEl: string;
  successWait: number;
  successMsgText: string;
  addToCartText: string;
  selector: string;
  cartAddedText: string;
  cartLoadingText: string;
  failedCartText: string;
}

type Options = Partial<AjaxAddOptions>;

export class AjaxAdd {
  static defaults: AjaxAddOptions = {
    addToCartText: 'add to basket',
    buttonEl: '[data-ajaxadd-button]',
    buttonTextEl: '[data-ajaxadd-button-text]',
    cartAddedText: 'added',
    cartLoadingText: 'adding...',
    failedCartText: 'unable to add product',
    selector: '[data-ajaxadd]',
    successMsgText: 'added to basket',
    successWait: 2000,
  };
  options: AjaxAddOptions;
  container: AjaxAddElement;
  buttonText: HTMLElement;
  button: HTMLButtonElement;
  submitHandler: EventListener;

  constructor(container: AjaxAddElement, options: Options = {}) {
    if (!container || container.ajaxadd) {
      return;
    }

    this.container = container;
    container.ajaxadd = this;

    this.options = {
      ...AjaxAdd.defaults,
      ...this.container.dataset,
      ...options,
    };

    this.options.successWait = Number(this.options.successWait);

    const button = this.container.querySelector(
      this.options.buttonEl,
    ) as HTMLButtonElement;
    const buttonText = this.container.querySelector(
      this.options.buttonTextEl,
    ) as HTMLElement;

    if (!button || !buttonText) {
      return;
    }

    this.button = button;
    this.buttonText = buttonText;

    this.bind();
  }

  bind() {
    this.submitHandler = this.handleSubmit.bind(this);
    this.container.addEventListener('submit', this.submitHandler);
  }

  handleSubmit(event: Event) {
    event.preventDefault();
    this.setLoading();

    const target = event.target as HTMLFormElement;
    const formData = serialize(target, {
      hash: true,
    });

    const {id, quantity, properties} = formData;

    if (typeof id !== 'string' || typeof quantity !== 'string') {
      return;
    }

    if (properties && !Array.isArray(properties)) {
      return;
    }

    const product = [
      {
        id: parseInt(id, 10),
        options: {
          properties,
          quantity,
        },
      },
    ];

    this.request(product);
  }

  async request(products: AddProduct[]) {
    try {
      const results = await addToCartInSequence(products);

      this.handleResponse(results);
    } catch (error) {
      this.restore();
    }
  }

  handleResponse(results: Response[]) {
    const failed = results
      .map((val: Response, index) => {
        return val.status ? index : null;
      })
      .filter(val => val !== null);

    if (failed.length >= 1) {
      this.dispatchFailure();
    }

    if (failed.length === results.length) {
      this.restore();
      return;
    }

    const event = new CustomEvent('cart:updated', {
      bubbles: true,
      detail: results,
    });

    this.container.dispatchEvent(event);
    this.setAdded();
  }

  dispatchFailure() {
    const messageEvent = new CustomEvent('message:updated', {
      bubbles: true,
      detail: {
        text: this.options.failedCartText,
        type: 'error',
      },
    });

    this.container.dispatchEvent(messageEvent);
  }

  setAdded() {
    this.buttonText.textContent = this.options.cartAddedText;
    window.setTimeout(this.restore.bind(this), this.options.successWait);
  }

  setLoading() {
    this.button.setAttribute('disabled', 'true');
    this.buttonText.textContent = this.options.cartLoadingText;
  }

  restore() {
    this.button.removeAttribute('disabled');
    this.buttonText.textContent = this.options.addToCartText;
  }

  destroy() {
    this.restore();
    this.container.removeEventListener('submit', this.submitHandler);
    this.container.ajaxadd = undefined;
  }
}
